import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store/store'

import i18n from '@/i18n'
import locales, { SUPPORTED_LOCALES } from '@/locales/locales.js'
import urlUtil from '@/js/utils/urlUtil'
import StringUtil from '@/js/utils/stringUtil'

import routes from './routes'
import { ParticipantPath, ParticipantSegment, MY_PAGES_ROOT_PATH } from './myPagesPaths'
import commonUtil from '@/js/utils/commonUtil'
import OrganizationRepository from '@/js/api/OrganizationRepository'
import { WIZARD_STEP_COMPLETED } from '@/js/api/private/TOrganizationRepository'

Vue.use(VueRouter)

/** Space kept above a section reached by a `#hash` link: the height of the fixed app bar plus a margin. */
const SECTION_SCROLL_OFFSET_PX = 80
/** Wait before scrolling to a `#hash` section, so a lazily loaded page has rendered it. */
const SECTION_SCROLL_DELAY_MS = 300

function getLocaleRegex () {
  let reg = ''
  SUPPORTED_LOCALES.forEach((locale, index) => {
    reg = `${reg}${locale.code}${index !== SUPPORTED_LOCALES.length - 1 ? '|' : ''}`
  })
  return `(${reg})`
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [{
    path: `/:locale${getLocaleRegex()}?`,
    component: {
      template: '<router-view/>'
    },
    children: routes
  }],
  scrollBehavior (to) {
    if (to.hash) {
      // A link to a section (e.g. a home feature card → /features#payments). The page is loaded
      // lazily, so wait a moment for the section to exist; the offset keeps it below the app bar.
      return new Promise(resolve => {
        setTimeout(() => resolve({ selector: to.hash, offset: { x: 0, y: SECTION_SCROLL_OFFSET_PX } }), SECTION_SCROLL_DELAY_MS)
      })
    }
    return { x: 0, y: 0 }
  }
})

/** This fix problem with router push to the same page (duplicate navigation) */
const originalPush = VueRouter.prototype.push 
VueRouter.prototype.push = function push(location) {
   return originalPush.call(this, location).catch(err => err)
}

router.beforeEach(async (to, from, next) => {
  // If user visits us with new session then set web language based on previous session (stored in local storage) or for first time visits based on browser language priority
  if (!from.name) { // If this is first visit to page (from.name will be null)
    store.dispatch('session/setLandingUrl', window.location.href)
    store.dispatch('session/setReferrerUrl', document.referrer)

    //Setup locale
    let useLanguage = commonUtil.findFirstNonNullArgument(urlUtil.getLangFromUrl(), localStorage.getItem('lang'), 'lv')
    if (!locales.isLanguageSupported(useLanguage)){
      useLanguage = 'lv'
    }

    let organizationIdenfier = urlUtil.getOrganizationIdentifier(to.path)
    if (organizationIdenfier && useLanguage !== 'lv'){
      useLanguage = 'lv'
    }

    let localeFromUrl = locales.getLocaleByCode(to.params.locale)
    if (useLanguage !== localeFromUrl.code
        || useLanguage !== localStorage.getItem('lang')
        || useLanguage !== i18n.locale){
      localStorage.setItem('lang', useLanguage)
      i18n.locale = useLanguage
      router.push(urlUtil.getUrlInLocale(useLanguage, urlUtil.getUrlWithoutLocale()))
      return
    }
  }

  //Check organization specific urls
  let organizationIdenfier = urlUtil.getOrganizationIdentifier(to.path)
  if (organizationIdenfier){
    let organizationByLocation = store.getters['session/organizationByLocation']
    if (!organizationByLocation || organizationIdenfier !== organizationByLocation.identifier){
      organizationByLocation = await OrganizationRepository.findByIdentifier(organizationIdenfier)
      // An unknown or closed studio continues to its URL: OrganizationLayout shows the not-found page
      // there, with noindex. Redirecting to the home page would serve indexable content at a dead URL [B31].
      store.dispatch('session/setOrganizationByLocation', organizationByLocation || null)
    }
  } else {
    if (store.getters['session/organizationByLocation'])
      store.dispatch('session/setOrganizationByLocation', null)
  }


  // Check authentificated user routes
  const isLoggedIn = store.getters['user/isLoggedIn']
  const user = store.getters['user/user']
  const currentAccountIdentifier = store.getters['dashboard/currentAccountIdentifier']
  const isAccountTypeOrganization = store.getters['dashboard/isAccountTypeOrganization']
  const isAccountTypeParticipant = store.getters['dashboard/isAccountTypeParticipant']
  const isEmailVerified = user && user.emailVerified === true

  //Validate authenticated user routes
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isLoggedIn) {
      // Preserve full path including query (e.g. verify-email?email=...&token=...) so email link flow works
      router.push({ path: urlUtil.getLocalisedUrl('/login'), query: { returnUrl: to.fullPath } })
      return
    }
    
    {/** Validate account or switch account if needed - check by url. When we switch, re-run the guard so all getters (e.g. isAccountTypeParticipant) reflect the new account. */
      let organizationIdentifier = to.params.orgIdentifier
      if (to.matched.some(record => record.meta.orgIdentifier === ParticipantSegment.ROOT)) {
        organizationIdentifier = ParticipantSegment.ROOT
      }
      if (organizationIdentifier != null && currentAccountIdentifier !== organizationIdentifier) {
        //Check if account exists, if not, set default account
        const account = store.getters['user/accountsForSwitcher'].find(account => StringUtil.equalsIgnoreCase(account.organizationIdentifier, organizationIdentifier))
        if (account) {
          await store.dispatch('dashboard/setAccount', account)
        } else {
          await store.dispatch('dashboard/setDefaultAccount')
          router.push({ path: urlUtil.getLocalisedMyPagesUrl() })
          return
        }
        next({ ...to, replace: true })
        return
      } else if (currentAccountIdentifier == null) {
        //No account selected, set default account
        await store.dispatch('dashboard/setDefaultAccount')
      }
    }

    const path = urlUtil.removeLastSlash(to.path)
    if (path === MY_PAGES_ROOT_PATH || path === urlUtil.getLocalisedUrl(MY_PAGES_ROOT_PATH)) {
      router.push({ path: urlUtil.getLocalisedMyPagesUrl(null) })
      return
    }

    if (isAccountTypeOrganization){
      const activeOrganization = store.getters['dashboard/activeOrganization']
      const isWizardCompleted = activeOrganization && activeOrganization.wizard_step === WIZARD_STEP_COMPLETED

      //If tries to acceess welcome wizard page, but has already completed the wizard, redirect to mypages page
      if (path === urlUtil.getLocalisedUrl('/welcome-wizard') && isWizardCompleted) {
        router.push({ path: urlUtil.getLocalisedMyPagesUrl() })
        return
        
      } else if (to.matched.some(record => record.meta.requiresOrganization)
        && path !== urlUtil.getLocalisedUrl('/welcome-wizard')
        && !isWizardCompleted
      ) {
        //Check if requires to finish welcome wizard before accessing the page
        router.push({ path: urlUtil.getLocalisedUrl('/welcome-wizard') })
        return
      }
    }

    if (isAccountTypeParticipant) {
      if (to.matched.some(record => record.meta.requiresEmailVerification) && !isEmailVerified) {
        // Only require email verification if the target route doesn't explicitly opt out (e.g. /mypages/participant  /email-verification has requiresEmailVerification: false)
        const leafRecord = to.matched[to.matched.length - 1]
        const targetExemptFromVerification = leafRecord && leafRecord.meta && leafRecord.meta.requiresEmailVerification === false
        if (!targetExemptFromVerification){
          router.push({ path: urlUtil.getLocalisedUrl(ParticipantPath.EMAIL_VERIFICATION) })
          return
        }
      }

      // For participants: allow only participant routes, or routes that are allowed without account context (e.g. user profile /mypages/account)
      const allowedWithoutParticipantContext = to.matched.some(record => record.meta.allowWithoutAccountContext === true)
      if (!allowedWithoutParticipantContext && !to.matched.some(record => record.meta.requiresParticipant)) {
        router.push({ path: urlUtil.getLocalisedUrl(ParticipantPath.DASHBOARD) })
        return
      }
    }

  } else {

    //Validate unauthenticated user routes with logged in user
    if (isLoggedIn){
      if (to.fullPath.includes(urlUtil.getLocalisedUrl('/login')) 
          || to.fullPath.includes(urlUtil.getLocalisedUrl('/signup'))){
        router.push({ path: urlUtil.getLocalisedMyPagesUrl() })
        return
      }

      if (to.fullPath.startsWith(urlUtil.getLocalisedUrl('/verify-email')) && isEmailVerified) {
        if (isAccountTypeParticipant) {
          router.push({ path: urlUtil.getLocalisedMyPagesUrl('participant-dashboard') })
          return
        }
        if (isAccountTypeOrganization) {
          router.push({ path: urlUtil.getLocalisedMyPagesUrl('events') })
          return
        }
      }

      if (to.fullPath.startsWith(urlUtil.getLocalisedUrl('/welcome-wizard'))) {
        const activeOrganization = store.getters['dashboard/activeOrganization']
        const isWizardCompleted = activeOrganization && activeOrganization.wizard_step === WIZARD_STEP_COMPLETED
        if (isWizardCompleted) {
          router.push({ path: urlUtil.getLocalisedMyPagesUrl('events') })
          return
        }
      }
    } else {

      if (to.name === 'new-organization') {
        router.push({ path: urlUtil.getLocalisedUrl('/signup') })
        return
      }

      if (to.fullPath.includes(urlUtil.getLocalisedUrl('/verify-email'))//TODO test if we need to check verify-email page for unauthenticated user
          || to.fullPath.includes(urlUtil.getLocalisedUrl('/welcome-wizard'))) {
        router.push({ path: urlUtil.getLocalisedUrl('/login') })
        return
      }
    }
  }
  
  next()
})

export default router
